import React, { useState, useEffect } from 'react';
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '@/components/ui/command';
import { Calendar, Music, CheckSquare } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../config';

const API = API_URL;

export const GlobalSearch = ({ open, onOpenChange, onNavigate }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({
    eventos: [],
    canciones: [],
    tareas: []
  });
  const [searching, setSearching] = useState(false);

  // Search function
  const performSearch = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults({ eventos: [], canciones: [], tareas: [] });
      return;
    }

    setSearching(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Search in parallel
      const [eventosRes, cancionesRes, tareasRes] = await Promise.allSettled([
        axios.get(`${API}/playlists`, { headers }).then(res => {
          const playlists = Array.isArray(res.data) ? res.data : [];
          return playlists.filter(p => 
            (p.nombre_novios?.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (p.lugar_evento?.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (p.nombre_dj?.toLowerCase().includes(searchQuery.toLowerCase()))
          ).slice(0, 5);
        }),
        axios.get(`${API}/songs?search=${searchQuery}&limit=5`, { headers }).then(res => {
          return res.data?.songs || res.data?.data || [];
        }),
        axios.get(`${API}/tasks`, { headers }).then(res => {
          const tasks = Array.isArray(res.data) ? res.data : [];
          return tasks.filter(t =>
            t.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            t.description?.toLowerCase().includes(searchQuery.toLowerCase())
          ).slice(0, 5);
        })
      ]);

      setResults({
        eventos: eventosRes.status === 'fulfilled' ? eventosRes.value : [],
        canciones: cancionesRes.status === 'fulfilled' ? cancionesRes.value : [],
        tareas: tareasRes.status === 'fulfilled' ? tareasRes.value : []
      });
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setSearching(false);
    }
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const handleSelect = (type, item) => {
    onNavigate(type, item);
    onOpenChange(false);
    setQuery('');
    setResults({ eventos: [], canciones: [], tareas: [] });
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput 
        placeholder="Buscar eventos, canciones, tareas..." 
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>
          {searching ? 'Buscando...' : 'No se encontraron resultados'}
        </CommandEmpty>

        {results.eventos.length > 0 && (
          <>
            <CommandGroup heading="📅 Eventos">
              {results.eventos.map((evento) => (
                <CommandItem
                  key={evento.id}
                  onSelect={() => handleSelect('evento', evento)}
                  className="cursor-pointer"
                >
                  <Calendar className="mr-2 h-4 w-4" />
                  <div className="flex-1">
                    <div className="font-medium">{evento.nombre_novios}</div>
                    <div className="text-xs text-slate-400">
                      {evento.fecha_evento} • {evento.lugar_evento || 'Sin lugar'}
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {results.canciones.length > 0 && (
          <>
            <CommandGroup heading="🎵 Canciones">
              {results.canciones.map((cancion) => (
                <CommandItem
                  key={cancion.id}
                  onSelect={() => handleSelect('cancion', cancion)}
                  className="cursor-pointer"
                >
                  <Music className="mr-2 h-4 w-4" />
                  <div className="flex-1">
                    <div className="font-medium">{cancion.title}</div>
                    <div className="text-xs text-slate-400">{cancion.artist}</div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {results.tareas.length > 0 && (
          <>
            <CommandGroup heading="✅ Tareas">
              {results.tareas.map((tarea) => (
                <CommandItem
                  key={tarea.id}
                  onSelect={() => handleSelect('tarea', tarea)}
                  className="cursor-pointer"
                >
                  <CheckSquare className="mr-2 h-4 w-4" />
                  <div className="flex-1">
                    <div className="font-medium">{tarea.title}</div>
                    <div className="text-xs text-slate-400">
                      {tarea.due_date || tarea.start_datetime?.split('T')[0] || 'Sin fecha'}
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
};

export default GlobalSearch;
